import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ExternalLink, Bell, Filter, X } from 'lucide-react';
import { SEO, SchemaOrg } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatUSD = (n) =>
  typeof n === 'number'
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n)
    : '—';

const timeAgo = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const discountPct = (price, retail) =>
  retail && retail > price ? Math.round(((retail - price) / retail) * 100) : 0;

export const DropsPage = () => {
  const [drops, setDrops] = useState([]);
  const [filters, setFilters] = useState({ designers: [], categories: [], partners: [] });
  const [active, setActive] = useState({ designer: '', category: '', partner: '' });
  const [totalActive, setTotalActive] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/drops/filters`);
        if (!cancelled) setFilters(res.data);
      } catch (e) {
        /* noop */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (active.designer) params.set('designer', active.designer);
        if (active.category) params.set('category', active.category);
        if (active.partner) params.set('partner', active.partner);
        const res = await axios.get(`${API}/drops?${params.toString()}`);
        if (!cancelled) {
          setDrops(res.data.drops);
          setTotalActive(res.data.total_active);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    window.scrollTo(0, 0);
    return () => {
      cancelled = true;
    };
  }, [active]);

  const setFilter = (key, val) => setActive((a) => ({ ...a, [key]: a[key] === val ? '' : val }));
  const clearAll = () => setActive({ designer: '', category: '', partner: '' });
  const hasFilters = active.designer || active.category || active.partner;

  const jsonLdItems = useMemo(
    () =>
      drops.slice(0, 20).map((d, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'Product',
          name: d.title,
          image: d.image_url,
          brand: { '@type': 'Brand', name: d.designer },
          offers: {
            '@type': 'Offer',
            price: d.price,
            priceCurrency: 'USD',
            availability: 'https://schema.org/InStock',
            url: d.affiliate_url,
          },
        },
      })),
    [drops]
  );

  return (
    <div data-testid="drops-page" className="bg-[#FAF9F6]">
      <SEO
        title="Drop Tracker — Fresh Pre-Owned Luxury Jewelry This Week"
        description="Curated fresh inventory from the world's best pre-owned luxury jewelry platforms — Fashionphile, The RealReal, 1stDibs, Rebag. Updated daily by our editorial team."
        url="/drops"
        type="website"
      />
      <SchemaOrg
        type="breadcrumb"
        data={{
          items: [
            { name: 'Home', url: '/' },
            { name: 'Drop Tracker', url: '/drops' },
          ],
        }}
      />
      {/* ItemList schema directly via SchemaOrg custom (use faqpage builder? we'll inject inline) */}
      {jsonLdItems.length > 0 && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'ItemList',
              itemListElement: jsonLdItems,
            }),
          }}
        />
      )}

      {/* Hero */}
      <section className="relative bg-[#111] text-white overflow-hidden">
        <div className="container-editorial pt-20 pb-16 lg:pt-28 lg:pb-20">
          <nav className="flex items-center gap-3 text-xs tracking-[0.15em] uppercase text-white/60 mb-12">
            <Link to="/" className="hover:text-[#C69C6D] transition-colors">Home</Link>
            <span>/</span>
            <span className="text-white">Drop Tracker</span>
          </nav>

          <div className="flex items-center gap-3 mb-6">
            <Bell className="w-5 h-5 text-[#C69C6D]" strokeWidth={1.5} />
            <p className="overline text-[#C69C6D]">New This Week</p>
          </div>
          <h1 className="font-heading font-light text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight mb-8">
            The <span className="italic">Drop</span> Tracker
          </h1>
          <p className="font-heading italic text-xl lg:text-2xl text-white/85 max-w-3xl mb-6">
            Fresh inventory, vetted platforms, curated daily.
          </p>
          <p className="text-white/70 max-w-2xl">
            Every listing below is a real piece live right now on one of our partner sites. We highlight the ones worth your attention — rare sizes, boxed + papers, significant discounts. <Link to="/affiliate-disclosure" className="text-[#C69C6D] hover:text-white underline underline-offset-4 transition-colors">Affiliate disclosure.</Link>
          </p>
          <p className="overline text-white/50 mt-10">{totalActive} active listings</p>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-[88px] z-30 bg-[#FAF9F6]/95 backdrop-blur-sm border-b border-[#E5E0D8]">
        <div className="container-editorial py-5">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-[#888] mr-2">
              <Filter className="w-3.5 h-3.5" strokeWidth={1.5} />
              Filter
            </div>

            {/* Designer chips */}
            {filters.designers.map((d) => (
              <button
                key={d}
                data-testid={`filter-designer-${d}`}
                onClick={() => setFilter('designer', d)}
                className={`text-[11px] tracking-[0.15em] uppercase px-3 py-2 border transition-colors ${
                  active.designer === d
                    ? 'bg-[#111] text-white border-[#111]'
                    : 'text-[#555] border-[#E5E0D8] hover:border-[#111] hover:text-[#111]'
                }`}
              >
                {d}
              </button>
            ))}

            <span className="h-5 w-px bg-[#E5E0D8] mx-1" />

            {/* Category chips */}
            {filters.categories.map((c) => (
              <button
                key={c}
                data-testid={`filter-category-${c}`}
                onClick={() => setFilter('category', c)}
                className={`text-[11px] tracking-[0.15em] uppercase px-3 py-2 border transition-colors ${
                  active.category === c
                    ? 'bg-[#111] text-white border-[#111]'
                    : 'text-[#555] border-[#E5E0D8] hover:border-[#111] hover:text-[#111]'
                }`}
              >
                {c}
              </button>
            ))}

            {hasFilters && (
              <button
                data-testid="clear-filters"
                onClick={clearAll}
                className="text-[11px] tracking-[0.15em] uppercase px-3 py-2 inline-flex items-center gap-2 text-[#9B2C2C] hover:bg-[#9B2C2C] hover:text-white border border-[#9B2C2C] transition-colors ml-auto"
              >
                <X className="w-3 h-3" strokeWidth={1.75} /> Clear
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="section-editorial">
        <div className="container-editorial">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-6 h-6 border border-[#111] border-t-transparent animate-spin" />
            </div>
          ) : drops.length === 0 ? (
            <div className="text-center py-20">
              <p className="overline overline-gold mb-3">No matches</p>
              <p className="font-heading text-2xl text-[#555]">Try clearing a filter — fresh drops every day.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-12">
              {drops.map((d) => {
                const pct = discountPct(d.price, d.retail_price);
                return (
                  <a
                    key={d.id}
                    href={d.affiliate_url}
                    target="_blank"
                    rel="sponsored noopener noreferrer"
                    data-testid={`drop-card-${d.id}`}
                    className="group block bg-white border border-[#E5E0D8] hover:border-[#111] transition-all duration-300"
                  >
                    <div className="relative aspect-square overflow-hidden img-zoom bg-[#F3EFE6]">
                      <img src={d.image_url} alt={d.title} className="w-full h-full object-cover" />
                      {pct > 0 && (
                        <div className="absolute top-3 left-3 bg-[#111] text-white text-[10px] tracking-[0.15em] uppercase px-2.5 py-1.5">
                          {pct}% off
                        </div>
                      )}
                      <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm text-[10px] tracking-[0.15em] uppercase text-[#111] px-2.5 py-1.5">
                        {d.condition}
                      </div>
                    </div>

                    <div className="p-5">
                      <p className="overline text-[10px] mb-2">{d.designer}</p>
                      <h3 className="font-heading text-lg leading-tight text-[#111] group-hover:text-[#C69C6D] transition-colors mb-4 min-h-[2.5rem] line-clamp-2">
                        {d.title}
                      </h3>

                      <div className="flex items-baseline gap-3 mb-4">
                        <p className="font-heading text-2xl text-[#111]">{formatUSD(d.price)}</p>
                        {d.retail_price && d.retail_price > d.price && (
                          <p className="text-sm text-[#888] line-through">{formatUSD(d.retail_price)}</p>
                        )}
                      </div>

                      {d.highlight && (
                        <p className="text-xs italic text-[#8B5A2B] mb-4">{d.highlight}</p>
                      )}

                      <div className="flex items-center justify-between pt-4 border-t border-[#E5E0D8]">
                        <span className="text-[10px] tracking-[0.15em] uppercase text-[#888]">
                          {d.partner} · {timeAgo(d.posted_at)}
                        </span>
                        <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.15em] uppercase text-[#C69C6D]">
                          View <ExternalLink className="w-3 h-3" strokeWidth={1.75} />
                        </span>
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#111] text-white py-16">
        <div className="container-editorial text-center">
          <p className="overline text-[#C69C6D] mb-4">Next</p>
          <h2 className="heading-editorial text-3xl lg:text-4xl text-white mb-6">
            See which pieces will <span className="italic">hold</span> their value
          </h2>
          <Link to="/investments" className="btn-editorial btn-editorial-inverse inline-flex">
            Open The Investment Index
          </Link>
        </div>
      </section>
    </div>
  );
};

export default DropsPage;
